import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  UserGroupIcon,
  CurrencyEuroIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { adminService, DashboardStats as APIDashboardStats, RecentBooking as APIRecentBooking } from '../../services/adminService';
import './AdminDashboard.css';

// T-109 (v1.8.27): Kacheln mit Beschriftungen, die zu den berechneten Werten passen (Kundin 24.09.:
// "Offene Zahlungen" war fest 0, "Monatsumsatz" das Volumen angelegter Buchungen, "Gesamtumsatz" nur der heutige Tag)
interface DashboardStats {
  todaysBookings: number;       // heute angelegte Buchungen
  openOptions: number;          // Buchungen mit Status Option oder Angebot gesendet
  monthlyBookingVolume: number; // Summe der im laufenden Monat angelegten Buchungen (Auftragsvolumen, nicht Zahlungseingang)
  newCustomersToday: number;    // Kunden mit erster Buchung heute
  weeklyBookings: number;       // in dieser Woche angelegte Buchungen
  openInvoicesTotal: number;    // Summe offener Rechnungen (nicht bezahlt, nicht storniert)
  openInvoicesCount: number;
  paymentsThisMonth: number;    // Zahlungseingänge im Monat (bezahlte Rechnungen)
  paymentsTotal: number;        // Zahlungseingänge gesamt
}

interface RecentBooking {
  id: string;
  bookingNumber?: string; // T-046: fuer den Details-Link auf die Buchungssuche
  customerName: string;
  eventDate: string;
  participants: number;
  status: 'pending' | 'confirmed' | 'cancelled';
  totalAmount: number;
}

interface SystemAlert {
  id: number;
  type: string;
  message: string;
  severity: 'info' | 'warning' | 'error';
  timestamp: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    todaysBookings: 0,
    openOptions: 0,
    monthlyBookingVolume: 0,
    newCustomersToday: 0,
    weeklyBookings: 0,
    openInvoicesTotal: 0,
    openInvoicesCount: 0,
    paymentsThisMonth: 0,
    paymentsTotal: 0
  });
  
  const [recentBookings, setRecentBookings] = useState<RecentBooking[]>([]);
  const [systemAlerts, setSystemAlerts] = useState<SystemAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch real data from API
      const dashboardData = await adminService.getDashboardStats();
      const systemStatus = await adminService.getSystemStatus();
      
      // Transform API data to component state (T-109: Werte aus dem Backend, keine Platzhalter)
      setStats({
        todaysBookings: dashboardData.today.newBookings || 0,
        openOptions: dashboardData.payments?.openOptions || 0,
        monthlyBookingVolume: dashboardData.thisMonth.revenue || 0,
        newCustomersToday: dashboardData.today.newCustomers || 0,
        weeklyBookings: dashboardData.thisWeek.newBookings || 0,
        openInvoicesTotal: dashboardData.payments?.openInvoicesTotal || 0,
        openInvoicesCount: dashboardData.payments?.openInvoicesCount || 0,
        paymentsThisMonth: dashboardData.payments?.paymentsThisMonth || 0,
        paymentsTotal: dashboardData.payments?.paymentsTotal || 0
      });

      // Transform recent bookings
      // T-010 (28.08.2026): eventDate wird HIER einmalig nach de-DE formatiert,
      // die Tabelle rendert den String direkt (vorher wurde der bereits deutsche
      // Datums-String unten erneut durch new Date() geparst -> "Invalid Date").
      const formatTripDate = (raw: string | null | undefined): string => {
        if (!raw) return '-';
        const parsed = new Date(raw);
        return isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('de-DE');
      };
      const transformedBookings: RecentBooking[] = dashboardData.recentBookings.map(booking => ({
        id: booking.id.toString(),
        bookingNumber: booking.bookingNumber,
        customerName: booking.customerName,
        eventDate: formatTripDate(booking.tripDate),
        participants: booking.participantCount,
        status: 'confirmed' as const, // Default status since API doesn't provide it directly
        totalAmount: booking.totalAmount
      }));
      setRecentBookings(transformedBookings);

      // Transform system alerts
      const transformedAlerts: SystemAlert[] = dashboardData.systemAlerts.map(alert => ({
        id: alert.id,
        type: alert.alertType,
        message: alert.message,
        severity: alert.severity.toLowerCase() as 'info' | 'warning' | 'error',
        timestamp: alert.createdAt
      }));
      setSystemAlerts(transformedAlerts);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Fehler beim Laden der Dashboard-Daten');
      
      // T-109 (v1.8.27): keine Platzhalterwerte mehr bei API-Fehlern, die Kacheln bleiben auf 0 und die Fehlermeldung wird angezeigt
      setRecentBookings([]);
      setSystemAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'badge-warning',
      confirmed: 'badge-success',
      cancelled: 'badge-danger'
    };
    return badges[status as keyof typeof badges] || 'badge-secondary';
  };

  // T-109 (v1.8.27): Beschriftungen passend zu den Werten; Tooltip erklärt die Berechnung
  const statsCards = [
    {
      title: 'Neue Buchungen (heute)',
      hint: 'Heute angelegte Buchungen (alle Status)',
      value: stats.todaysBookings,
      icon: CalendarIcon,
      color: 'blue',
      link: '/admin/bookings?filter=today'
    },
    {
      title: 'Offene Optionen',
      hint: 'Vorläufige Buchungen mit Status Option (mit Stichtag); Angebote zählen nicht',
      value: stats.openOptions,
      icon: ClockIcon,
      color: 'yellow',
      link: '/admin/bookings?status=option'
    },
    {
      title: 'Buchungsvolumen (Monat)',
      hint: 'Summe der in diesem Monat angelegten Buchungen, unabhängig von Zahlungen',
      value: formatCurrency(stats.monthlyBookingVolume),
      icon: CurrencyEuroIcon,
      color: 'green',
      link: '/admin/bookings'
    },
    {
      title: 'Neue Kunden (heute)',
      hint: 'Kunden mit einer heute angelegten Buchung',
      value: stats.newCustomersToday,
      icon: UsersIcon,
      color: 'purple',
      link: '/admin/customers'
    }
  ];

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Dashboard wird geladen...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p className="dashboard-subtitle">Willkommen zurück! Hier ist Ihre Übersicht.</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {statsCards.map((card, index) => (
          <div
            key={index}
            className={`stat-card stat-card-${card.color}`}
            onClick={() => navigate(card.link)}
          >
            <div className="stat-icon">
              <card.icon className="stat-card-icon" />
            </div>
            <div className="stat-content" title={card.hint}>
              <h3>{card.title}</h3>
              <p className="stat-value">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Schnellzugriff</h2>
        <div className="action-buttons">
          <button
            className="action-btn btn-primary"
            onClick={() => navigate('/admin/booking-new')}
          >
            <CalendarIcon className="action-icon" />
            Neue Buchung
          </button>
          <button
            className="action-btn btn-yellow"
            onClick={() => navigate('/admin/customers', { state: { openDialog: true } })}
          >
            <UserGroupIcon className="action-icon" />
            Neuer Kunde
          </button>
          <button
            className="action-btn btn-info"
            onClick={() => navigate('/admin/invoices')}
          >
            <DocumentTextIcon className="action-icon" />
            Rechnungen
          </button>
          {/* T-046 (11.09.2026): "Berichte" ausgeblendet, stattdessen Schnellzugriff auf die E-Mail-Freigabe */}
          <button
            className="action-btn btn-success"
            onClick={() => navigate('/admin/email-queue')}
          >
            <ChartBarIcon className="action-icon" />
            E-Mails
          </button>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="recent-section">
        <div className="section-header">
          <h2>Aktuelle Buchungen</h2>
          <button
            className="btn-link"
            onClick={() => navigate('/admin/bookings')}
          >
            Alle anzeigen →
          </button>
        </div>
        
        <div className="bookings-table">
          <table>
            <thead>
              <tr>
                <th>Kunde</th>
                <th>Datum</th>
                <th>Teilnehmer</th>
                <th>Status</th>
                <th>Betrag</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {recentBookings.map((booking) => (
                <tr key={booking.id}>
                  <td>{booking.customerName}</td>
                  <td>{booking.eventDate}</td>
                  <td>{booking.participants}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(booking.status)}`}>
                      {booking.status === 'pending' ? 'Ausstehend' :
                       booking.status === 'confirmed' ? 'Bestätigt' : 'Storniert'}
                    </span>
                  </td>
                  <td>{formatCurrency(booking.totalAmount)}</td>
                  <td>
                    <button
                      className="btn-small btn-view"
                      // T-046: die Route /admin/bookings/{id} gab es nie (leere Seite), jetzt Buchungssuche
                      onClick={() => navigate(`/admin/bookings?search=${encodeURIComponent(booking.bookingNumber || booking.id)}`)}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="additional-stats">
        {/* T-109 (v1.8.27): Zahlungskennzahlen aus den Rechnungen statt Platzhalter */}
        <div className="stat-box" title="In dieser Woche angelegte Buchungen (alle Status)">
          <CheckCircleIcon className="additional-stat-icon text-green-500" />
          <div>
            <h3>Neue Buchungen (Woche)</h3>
            <p className="stat-number">{stats.weeklyBookings}</p>
          </div>
        </div>
        <div className="stat-box" title="Summe der offenen Beträge aller nicht bezahlten Rechnungen (wie in der Rechnungsverwaltung)">
          <ExclamationCircleIcon className="additional-stat-icon text-yellow-500" />
          <div>
            <h3>Offene Zahlungen ({stats.openInvoicesCount} Rechnungen)</h3>
            <p className="stat-number">{formatCurrency(stats.openInvoicesTotal)}</p>
          </div>
        </div>
        <div className="stat-box" title="Zahlungseingänge auf Rechnungen, die in diesem Monat vollständig bezahlt wurden">
          <CurrencyEuroIcon className="additional-stat-icon text-blue-500" />
          <div>
            <h3>Zahlungseingänge (Monat)</h3>
            <p className="stat-number">{formatCurrency(stats.paymentsThisMonth)}</p>
          </div>
        </div>
        <div className="stat-box" title="Alle auf Rechnungen verbuchten Zahlungen (Bankabgleich, Zahlung erfassen, Online-Zahlung)">
          <CurrencyEuroIcon className="additional-stat-icon text-green-500" />
          <div>
            <h3>Zahlungseingänge gesamt</h3>
            <p className="stat-number">{formatCurrency(stats.paymentsTotal)}</p>
          </div>
        </div>
      </div>

      {/* System Alerts */}
      {systemAlerts.length > 0 && (
        <div className="alerts-section">
          <div className="section-header">
            <h2>System-Benachrichtigungen</h2>
          </div>
          <div className="alerts-list">
            {systemAlerts.map((alert) => (
              <div key={alert.id} className={`alert alert-${alert.severity}`}>
                <div className="alert-content">
                  <span className="alert-type">{alert.type}</span>
                  <span className="alert-message">{alert.message}</span>
                </div>
                <span className="alert-time">
                  {new Date(alert.timestamp).toLocaleString('de-DE')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <ExclamationCircleIcon className="error-icon" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;